import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const MoonlightSonataViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Moonlight Sonata
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BeethovenLv/O27/moonlight/moonlight-a4.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Beethoven - Moonlight Sonata.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4987">
      <Navbar data-id="element-4988" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4989">
        {/* Hero Banner with Moonlit Piano Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4990">
          <img src="https://cdn.magicpatterns.com/uploads/pgCP79fYf6NDMcwEAPeHXy/IMG_4444.jpg" alt="Moonlight Sonata" className="w-full h-64 md:h-80 object-cover" data-id="element-4991" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4992" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4993">
            <div className="max-w-7xl mx-auto" data-id="element-4994">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4995">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4996" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4997">
                Moonlight Sonata
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4998">
                Piano Sonata No. 14 in C♯ minor, Op. 27, No. 2
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4999">
                Ludwig van Beethoven
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-5000">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-5001">
          <div className="flex items-center space-x-2" data-id="element-5002">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-5003">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-5004" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5005">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-5006">
                  <ZoomInIcon className="h-5 w-5" data-id="element-5007" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-5008">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-5009">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-5010" />
              <span className="hidden sm:inline" data-id="element-5011">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-5012">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-5013" />
              <span className="hidden sm:inline" data-id="element-5014">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-5015">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5016">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-5017">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Moonlight Sonata Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-5018" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5019">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5020">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-5021">
            <div data-id="element-5022">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5023">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-5024">
                <div data-id="element-5025">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5026">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5027">
                    Ludwig van Beethoven
                  </dd>
                </div>
                <div data-id="element-5028">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5029">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5030">
                    1801
                  </dd>
                </div>
                <div data-id="element-5031">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5032">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5033">
                    C♯ minor
                  </dd>
                </div>
                <div data-id="element-5034">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5035">
                    Opus
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5036">
                    Op. 27, No. 2
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-5037">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5038">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5039">
                The Piano Sonata No. 14 in C♯ minor, marked Quasi una fantasia,
                is one of Beethoven's most popular compositions. The name
                "Moonlight Sonata" was given by the German music critic and poet
                Ludwig Rellstab five years after Beethoven's death. The piece is
                known for its hauntingly beautiful first movement, which should
                be played with a gentle, flowing character.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-5040" />
    </div>;
};